import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { Collapse, Badge } from "reactstrap";
import { Trans } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import propTypes from "prop-types";
import * as actions from "../../store/actions/actions";

const Sidebar = ({ token, actions }) => {
  const [userMenu, setUserMenu] = useState([]);
  const [collapse, setCollapse] = useState({});
  const location = useLocation();
  const history = useHistory();

  const routeActive = useCallback(
      (paths) => {
        paths = Array.isArray(paths) ? paths : [paths];
        return paths.some((p) => location.pathname.indexOf(p) > -1);
      },
      [location.pathname]
  );

  const toggleItemCollapse = (stateName) => {
    for (let c in collapse) {
      if (collapse[c] === true && c !== stateName) {
        setCollapse({
          [c]: false,
        });
      }
    }
    setCollapse((prevState) => ({
      ...prevState,
      [stateName]: !prevState[stateName],
    }));
  };

  const itemType = (item) => {
    if (item.heading) return "heading";
    if (!item.submenu) return "menu";
    if (item.submenu) return "submenu";
  };

  const getSubRoutes = (item) => item.submenu.map(({ path }) => path);

  const SidebarItemHeader = ({ item }) => (
      <li className="nav-heading">
      <span>
        <Trans i18nKey={item.translate}>{item.heading}</Trans>
      </span>
      </li>
  );

  const SidebarItem = ({ item, isActive }) => (
      <li className={isActive ? "active" : ""}>
        <Link to={item.path} title={item.name}>
          {item.label && (
              <Badge tag="div" className="float-right" color={item.label.color}>
                {item.label.value}
              </Badge>
          )}
          {item.icon && <em className={item.icon}></em>}
          <span>
          <Trans i18nKey={item.translate}>{item.name}</Trans>
        </span>
        </Link>
      </li>
  );

  const SidebarSubItem = ({ item, isActive, handler, children, isOpen }) => (
      <li className={isActive ? "active" : ""}>
        <button
            type="button"
            className="nav-item sidebar-sub-toggle"
            onClick={handler}
            style={{ background: "none", border: "none", width: "100%", textAlign: "left" }}
        >
          {item.icon && <em className={item.icon}></em>}
          <span>
        <Trans i18nKey={item.translate}>{item.name}</Trans>
      </span>
        </button>
        <Collapse isOpen={isOpen}>
          <ul className="sidebar-nav sidebar-subnav">
            <SidebarSubHeader item={item} />
            {children}
          </ul>
        </Collapse>
      </li>
  );

  const SidebarSubHeader = ({ item }) => (
      <li className="sidebar-subnav-header">{item.name}</li>
  );

  const closeSidebar = useCallback(() => {
    actions.toggleSetting("asideToggled");
  }, [actions]);

  const buildCollapseList = useCallback(() => {
    let collapseState = {};
    userMenu
        .filter(({ heading }) => !heading)
        .forEach(({ name, path, submenu }) => {
          collapseState[name] = routeActive(
              submenu ? submenu.map(({ path }) => path) : path
          );
        });
    setCollapse(collapseState);
  }, [routeActive, userMenu]);

  const buildMenu = useCallback(() => {
    if (!Array.isArray(token) || token.length === 0) return;

    const user = token[0];
    const Menu = [];
    const modules = [];

    if (user.role_id === 1) {
      Menu.push({
        name: "DASHBOARD",
        icon: "icon-speedometer",
        path: "/mainDashboard",
      });

      Menu.push({
        name: "USER MGT.",
        icon: "icon-people",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "System Users",
            translate: "sidebar.nav.SUBMENU",
            path: "/users/user"
          },
          {
            name: "Gov. Officials",
            translate: "sidebar.nav.SUBMENU",
            path: "/users/government-officials"
          },
          {
            name: "Roles",
            translate: "sidebar.nav.SUBMENU",
            path: "/users/roles"
          },
          {
            name: "Permissions",
            translate: "sidebar.nav.SUBMENU",
            path: "/users/permissions"
          },
          {
            name: "Modules",
            translate: "sidebar.nav.SUBMENU",
            path: "/users/modules"
          }
        ]
      });

      Menu.push({
        name: "SETTINGS",
        icon: "icon-settings",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "General",
            translate: "sidebar.nav.SUBMENU",
            path: "/regulators"
          },
          {
            name: "PMS",
            translate: "sidebar.nav.SUBMENU",
            path: "/capitals"
          },
          {
            name: "BMS",
            translate: "sidebar.nav.SUBMENU",
            path: "/position"
          },
          {
            name: "FARS",
            translate: "sidebar.nav.SUBMENU",
            path: "/templates"
          }
        ]
      });

      Menu.push({
        name: "NTRC",
        icon: "icon-briefcase",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Collection",
            translate: "sidebar.nav.MENU",
            path: "/ntr/collection",
          },
          {
            name: "Budget",
            translate: "sidebar.nav.MENU",
            path: "/ntr/budget",
          },
          {
            name: "Billing",
            translate: "sidebar.nav.MENU",
            path: "/ntr/bill_status",
          },
        ],
      });

      Menu.push({
        name: "FARS",
        icon: "icon-briefcase",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Quarterly reports",
            translate: "sidebar.nav.MENU",
            path: "/fars/quarterly_statements"
          },
          {
            name: "Audited Reports",
            translate: "sidebar.nav.MENU",
            path: "/fars/audited_statements"
          },
          {
            name: "Analysis",
            translate: "sidebar.nav.MENU",
            path: "/fars/financial-analysis"
          }
        ]
      });

      Menu.push({
        name: "BMS",
        icon: "icon-briefcase",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Prospective Members",
            translate: "sidebar.nav.SUBMENU",
            path: "/appointee"
          },
          {
            name: "Board Members",
            translate: "sidebar.nav.SUBMENU",
            path: "/appointments"
          },
          {
            name: "Representatives",
            translate: "sidebar.nav.SUBMENU",
            path: "/representatives"
          },
          {
            name: "Repr. Reg-status",
            translate: "sidebar.nav.SUBMENU",
            path: "/rep_progress"
          },
          {
            name: "Member Reg-status",
            translate: "sidebar.nav.SUBMENU",
            path: "/appointmentProgress"
          }
        ]
      });

      Menu.push({
        name: "PSC DETAILS",
        icon: "icon-list",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Registration",
            translate: "sidebar.nav.SUBMENU",
            path: "/piscs"
          },
          {
            name: "Management",
            translate: "sidebar.nav.SUBMENU",
            path: "/management"
          }
        ]
      });

      setUserMenu(Menu);

    }else if(token[0].role_name.toLowerCase() === "otr admin") {

      Menu.push({
        name: "DASHBOARD",
        icon: "icon-speedometer",
        path: "/mainDashboard"
      });

      Menu.push({
        name: "USER MGT.",
        icon: "icon-people",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "System Users",
            translate: "sidebar.nav.SUBMENU",
            path: "/users/user"
          },
          {
            name: "Govn. Officials",
            translate: "sidebar.nav.SUBMENU",
            path: "/users/government-officials"
          }
        ]
      });

      Menu.push({
        name: "SETTINGS",
        icon: "icon-settings",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "General",
            translate: "sidebar.nav.SUBMENU",
            path: "/regulators"
          },
          {
            name: "PMS",
            translate: "sidebar.nav.SUBMENU",
            path: "/capitals"
          },
          {
            name: "BMS",
            translate: "sidebar.nav.SUBMENU",
            path: "/position"
          },
          {
            name: "FARS",
            translate: "sidebar.nav.SUBMENU",
            path: "/templates"
          }
        ]
      });

      Menu.push({
        name: "NTRC",
        icon: "icon-briefcase",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Collection",
            translate: "sidebar.nav.MENU",
            path: "/ntr/collection",
          },
          {
            name: "Budget",
            translate: "sidebar.nav.MENU",
            path: "/ntr/budget",
          },
          {
            name: "Billing",
            translate: "sidebar.nav.MENU",
            path: "/ntr/bill_status",
          },
        ],
      });

      Menu.push({
        name: "FARS",
        icon: "icon-briefcase",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Quarterly reports",
            translate: "sidebar.nav.MENU",
            path: "/fars/quarterly_statements"
          },
          {
            name: "Audited Reports",
            translate: "sidebar.nav.MENU",
            path: "/fars/audited_statements"
          },
          {
            name: "Analysis",
            translate: "sidebar.nav.MENU",
            path: "/fars/financial-analysis"
          }
        ]
      });

      Menu.push({
        name: "BMS",
        icon: "icon-briefcase",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Prospective Members",
            translate: "sidebar.nav.SUBMENU",
            path: "/appointee"
          },
          {
            name: "Board Members",
            translate: "sidebar.nav.SUBMENU",
            path: "/appointments"
          },
          {
            name: "Representatives",
            translate: "sidebar.nav.SUBMENU",
            path: "/representatives"
          },
          {
            name: "Repr. Reg-status",
            translate: "sidebar.nav.SUBMENU",
            path: "/rep_progress"
          },
          {
            name: "Member Reg-status",
            translate: "sidebar.nav.SUBMENU",
            path: "/appointmentProgress"
          }
        ]
      });

      Menu.push({
        name: "PSC DETAILS",
        icon: "icon-list",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Registration",
            translate: "sidebar.nav.SUBMENU",
            path: "/piscs"
          },
          {
            name: "Management",
            translate: "sidebar.nav.SUBMENU",
            path: "/management"
          }
        ]
      });

      setUserMenu(Menu);

    }else if(token[0].role_name.toLowerCase() === "pisc admin") {
      Menu.push({
        name: "DASHBOARD",
        icon: "icon-speedometer",
        path: "/home"
      });

      Menu.push({
        name: "SYSTEM USERS",
        icon: "icon-people",
        translate: "sidebar.nav.MENU",
        path: "/users/user"
      });

      Menu.push({
        name: "BOARD MEMBERS",
        icon: "icon-list",
        translate: "sidebar.nav.SUBMENU",
        path: "/appointments"
      });

      Menu.push({
        name: "INSTITUTIONS",
        icon: "icon-list",
        translate: "sidebar.nav.MENU",
        submenu: [
          {
            name: "Details",
            translate: "sidebar.nav.SUBMENU",
            path: "/pisc_establishment"
          },
          {
            name: "Addresses",
            translate: "sidebar.nav.SUBMENU",
            path: "/pisc_addresses"
          }
        ]
      });

      setUserMenu(Menu);

    }else{

      token.map(module => {
        if(module != null && module.module_name != null) {
          modules.push(module.module_name.toLowerCase());
        }
      });

      if(token[0].user_firstLogin === 1) {
        if(token[0].user_pisc_id === 1){
          Menu.push({
            name: "DASHBOARD",
            icon: "icon-speedometer",
            path: "/mainDashboard"
          });
        }else if(token[0].user_pisc_id === 0){
          Menu.push({
            name: "DASHBOARD",
            icon: "icon-speedometer",
            path: "/official-dashboard"
          });
        }else{
          Menu.push({
            name: "DASHBOARD",
            icon: "icon-speedometer",
            path: "/home"
          });
        }

        if (modules.includes("financial statements")) {
          if(token[0].role_name.toLowerCase() === "dpim user" ||
              token[0].role_name.toLowerCase() === "dpim admin" ||
              token[0].role_name.toLowerCase() === "dpim"){

            Menu.push({
              name: "FARS",
              icon: "icon-briefcase",
              translate: "sidebar.nav.MENU",
              submenu: [
                {
                  name: "Quarterly reports",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/quarterly_statements"
                },
                {
                  name: "Audited Reports",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/audited_statements"
                },
                {
                  name: "Analysis",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/financial-analysis"
                }
              ]
            });

            Menu.push({
              name: "NTRC",
              icon: "icon-briefcase",
              translate: "sidebar.nav.MENU",
              submenu: [
                {
                  name: "Collection",
                  translate: "sidebar.nav.MENU",
                  path: "/ntr/collection",
                },
                {
                  name: "Budget",
                  translate: "sidebar.nav.MENU",
                  path: "/ntr/budget",
                },
                {
                  name: "Billing",
                  translate: "sidebar.nav.MENU",
                  path: "/ntr/bill_status",
                },
              ],
            });

          }else if(token[0].role_name.toLowerCase() === "data collector" ||
              token[0].role_name.toLowerCase() === "data verifier"){
            Menu.push({
              name: "FARS",
              icon: "icon-briefcase",
              translate: "sidebar.nav.MENU",
              submenu: [
                {
                  name: "Audited Reports",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/audited_statements"
                }
              ]
            });
          }else if(token[0].role_name.toLowerCase() === "plo - data collector" ||
              token[0].role_name.toLowerCase() === "plo - data verifier"){
            Menu.push({
              name: "FARS",
              icon: "icon-briefcase",
              translate: "sidebar.nav.MENU",
              submenu: [
                {
                  name: "Quarterly reports",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/quarterly_statements"
                }
              ]
            });
          }else if(token[0].role_name.toLowerCase() === "ceo"){
            Menu.push({
              name: "FARS",
              icon: "icon-briefcase",
              translate: "sidebar.nav.MENU",
              submenu: [
                {
                  name: "Quarterly reports",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/quarterly_statements"
                },
                {
                  name: "Audited Reports",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/audited_statements"
                }
              ]
            });
          }else if(token[0].role_name.toLowerCase() === "ministry user"){
            Menu.push({
              name: "FARS",
              icon: "icon-briefcase",
              translate: "sidebar.nav.MENU",
              submenu: [
                {
                  name: "Analysis",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/financial-analysis"
                }
              ]
            });
          }else if(token[0].role_name.toLowerCase() === "tr"){
            Menu.push({
              name: "FARS",
              icon: "icon-briefcase",
              translate: "sidebar.nav.MENU",
              submenu: [
                {
                  name: "Quarterly reports",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/quarterly_statements"
                },
                {
                  name: "Audited Reports",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/audited_statements"
                },
                {
                  name: "Analysis",
                  translate: "sidebar.nav.MENU",
                  path: "/fars/financial-analysis"
                }
              ]
            });
          }
        }

        if (modules.includes("representatives")) {
          Menu.push({
            name: "BMS",
            icon: "icon-user-following",
            translate: "sidebar.nav.MENU",
            submenu: [
              {
                name: "Prospective Members",
                translate: "sidebar.nav.SUBMENU",
                path: "/appointee"
              },
              {
                name: "Representatives",
                translate: "sidebar.nav.SUBMENU",
                path: "/representatives"
              },
              {
                name: "Registration Status",
                translate: "sidebar.nav.SUBMENU",
                path: "/rep_progress"
              }
            ]
          });
        }

        if (modules.includes("appointments")) {

          const boardSubmenu = [];

          //STARTING OF DMS MENU
          if (token[0].role_name.toLowerCase() === 'msd user'){
            boardSubmenu.push({
                  name: "Prospective Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointee"
                },
                {
                  name: "Board Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointments"
                },
                {
                  name: "Registration Status",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointmentProgress"
                }
            );
          }

          if(token[0].role_name.toLowerCase() === 'dms' ||
              token[0].role_name.toLowerCase() === 'msd admin'){
            boardSubmenu.push(
                {
                  name: "Prospective Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointee"
                },
                {
                  name: "Board Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointments"
                },
                {
                  name: "Registration Status",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointmentProgress"
                }
            );
          }

          if(token[0].role_name.toLowerCase() === 'tr' ||
              token[0].role_name.toLowerCase() === 'government official' ||
              token[0].role_name.toLowerCase() === 'ministry user'){
            boardSubmenu.push(
                {
                  name: "Board Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointments"
                },
                {
                  name: "Representatives",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/representatives"
                },
            );
          }

          //STARTING OF PSC MENU
          if (token[0].role_name.toLowerCase() === 'board coordinator'){
            boardSubmenu.push({
                  name: "Prospective Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointee"
                },
                {
                  name: "Current Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointments"
                },
                {
                  name: "Previous Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/previousBoardMembers/"+ this.getEncodedUrl(token[0].user_pisc_id)
                },
                {
                  name: "Registration Status",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointmentProgress"
                }
            );
          }

          if(token[0].role_name.toLowerCase() === 'ahrm user' ||
              token[0].role_name.toLowerCase() === 'ahrm admin' ||
              token[0].role_name.toLowerCase() === 'data collector' ||
              token[0].role_name.toLowerCase() === 'data verifier' ||
              token[0].role_name.toLowerCase() === 'plo - data collector' ||
              token[0].role_name.toLowerCase() === 'plo - data verifier' ||
              token[0].role_name.toLowerCase() === 'ceo'){
            boardSubmenu.push(
                {
                  name: "Current Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/appointments"
                },
                {
                  name: "Previous Members",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/previousBoardMembers/"+ this.getEncodedUrl(token[0].user_pisc_id)
                }
            );
          }

          Menu.push({
            name: "BMS",
            icon: "icon-briefcase",
            translate: "sidebar.nav.MENU",
            submenu: boardSubmenu
          });
        }

        if(modules.includes("pisc")) {

          const institutionSubmenu = [];

          if(token[0].role_name.toLowerCase() === 'tr' ||
              token[0].role_name.toLowerCase() === 'government official' ||
              token[0].role_name.toLowerCase() === 'ministry user'){

            institutionSubmenu.push(
                {
                  name: "PSCs",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/piscs"
                },
                {
                  name: "Minority Interests",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/minority_interests"
                },
                {
                  name: "Priv. Enterprises",
                  translate: "sidebar.nav.MENU",
                  path: "/privatized_enterprises"
                },
                {
                  name: "PSC's Investments",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/pisc_investments"
                }
            );

            Menu.push({
              name: "INSTITUTIONS",
              icon: "icon-list",
              translate: "sidebar.nav.MENU",
              submenu: institutionSubmenu
            });
          }

          //STARTING OF DPIM MENU
          if (token[0].role_name.toLowerCase() === 'dpim user' ||
              token[0].role_name.toLowerCase() === 'dpim admin' ||
              token[0].role_name.toLowerCase() === 'dpim'){

            institutionSubmenu.push(
                {
                  name: "PSCs",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/piscs"
                },
                {
                  name: "MIs",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/minority_interests"
                },
                {
                  name: "PSC's Investments",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/pisc_investments"
                }
            );

            Menu.push({
              name: "INSTITUTIONS",
              icon: "icon-list",
              translate: "sidebar.nav.MENU",
              submenu: institutionSubmenu
            });
          }

          //STARTING OF MSD MENU
          if(token[0].role_name.toLowerCase() === 'msd user' ||
              token[0].role_name.toLowerCase() === 'msd admin' ||
              token[0].role_name.toLowerCase() === 'dms'){

            institutionSubmenu.push(
                {
                  name: "Registration",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/piscs"
                },
                {
                  name: "Management",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/management"
                }
            );

            Menu.push({
              name: "PSC DETAILS",
              icon: "icon-list",
              translate: "sidebar.nav.MENU",
              submenu: institutionSubmenu
            });
          }

          //STARTING OF DPMERD MENU
          if(token[0].role_name.toLowerCase() === 'dpmerd user' ||
              token[0].role_name.toLowerCase() === 'dpmerd admin' ||
              token[0].role_name.toLowerCase() === 'dpmerd'){
            Menu.push({
              name: "PE DETAILS",
              icon: "icon-list",
              translate: "sidebar.nav.MENU",
              path: "/privatized_enterprises"
            });
          }

          //STARTING OF PSC MENU
          if (token[0].role_name.toLowerCase() === 'board coordinator' ||
              token[0].role_name.toLowerCase() === 'ahrm user' ||
              token[0].role_name.toLowerCase() === 'ahrm admin' ||
              token[0].role_name.toLowerCase() === 'data collector' ||
              token[0].role_name.toLowerCase() === 'data verifier' ||
              token[0].role_name.toLowerCase() === 'plo - data collector' ||
              token[0].role_name.toLowerCase() === 'plo - data verifier' ||
              token[0].role_name.toLowerCase() === 'ceo'){
            institutionSubmenu.push(
                {
                  name: "Institution Details",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/pisc_establishment"
                },
                {
                  name: "Contacts",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/pisc_addresses"
                }
            );

            Menu.push({
              name: "PSC DETAILS",
              icon: "icon-list",
              translate: "sidebar.nav.MENU",
              submenu: institutionSubmenu
            });
          }
        }
        if(modules.includes("settings")){
          Menu.push({
            name: "SETTINGS",
            icon: "icon-settings",
            translate: "sidebar.nav.MENU",
            submenu: [
              {
                name: "Subsidiaries",
                translate: "sidebar.nav.SUBMENU",
                path: "/companies"
              },
              {
                name: "Equity Issuers",
                translate: "sidebar.nav.SUBMENU",
                path: "/equity-issuers"
              },
              {
                name: "JV Companies",
                translate: "sidebar.nav.SUBMENU",
                path: "/joint-ventures"
              }
            ]
          });
        }

        if (modules.includes("archive")) {

          const archiveSubmenu = [];

          if(token[0].role_name.toLowerCase() === 'board coordinator'){
            archiveSubmenu.push({
                  name: "Uploading",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/upload"
                },
                {
                  name: "Board Documents",
                  translate: "sidebar.nav.SUBMENU",
                  path: "/piscDocuments/"+ this.getEncodedUrl(token[0].user_pisc_id)
                });
          }
          if(token[0].role_name.toLowerCase() === 'ceo'){
            archiveSubmenu.push({
              name: "Board Documents",
              translate: "sidebar.nav.SUBMENU",
              path: "/piscDocuments/"+ this.getEncodedUrl(token[0].user_pisc_id)
            });
          }

          if(token[0].role_name.toLowerCase() === 'tr' ||
              token[0].role_name.toLowerCase() === 'government official' ||
              token[0].role_name.toLowerCase() === 'ministry user' ||
              token[0].role_name.toLowerCase() === 'msd user' ||
              token[0].role_name.toLowerCase() === 'msd admin' ||
              token[0].role_name.toLowerCase() === 'dms' ||
              token[0].role_name.toLowerCase() === 'dpim user' ||
              token[0].role_name.toLowerCase() === 'dpim admin' ||
              token[0].role_name.toLowerCase() === 'dpim'){
            archiveSubmenu.push({
              name: "Board Members",
              translate: "sidebar.nav.SUBMENU",
              path: "/previousBoards"
            },{
              name: "Management",
              translate: "sidebar.nav.SUBMENU",
              path: "/previousManagement"
            });
          }
        }
      }

      if(token[0].role_name.toLowerCase() === 'board member') {
        Menu.push({
          name: "HOME",
          icon: "icon-user",
          path: "/member_profile/"+ this.getEncodedUrl(token[0].id) +"/0"
        });

        Menu.push({
          name: "REG. STATUS",
          icon: "icon-list",
          path: "/appointmentProgress"
        });

        this.setState({
          setUserMenu: Menu
        });
      }
    }

    Menu.push({
      name: "REPORTS",
      icon: "icon-chart",
      translate: "sidebar.nav.MENU",
      submenu: [
        {
          name: "NTRC",
          translate: "sidebar.nav.SUBMENU",
          path: "/ntrCollection"
        },
        {
          name: "BMS",
          translate: "sidebar.nav.SUBMENU",
          path: "/boardManagement"
        },
        {
          name: "FARS",
          translate: "sidebar.nav.SUBMENU",
          path: "/financialManagement"
        },
        {
          name: "OTR Portifolio",
          translate: "sidebar.nav.SUBMENU",
          path: "/portifolioManagement"
        },
        {
          name: "PSC Management",
          translate: "sidebar.nav.SUBMENU",
          path: "/pscManagement"
        }
      ]
    });

    Menu.push({
      name: "LOGOUT",
      icon: "icon-logout",
      path: "/logout"
    });

    setUserMenu(Menu);
  }, [token]);

  useEffect(() => {
    if (Array.isArray(token) && token.length > 0) {
      buildMenu();
    }
  }, [token, buildMenu]);

  useEffect(() => {
    buildCollapseList();

    const unlisten = history.listen(() => {
      setTimeout(() => {
        closeSidebar();
      }, 0);
    });

    return () => unlisten();
  }, [history, closeSidebar, buildCollapseList]);

  return (
      <aside className="aside-container">
        <div className="aside-inner">
          <nav className="sidebar">
            <ul className="sidebar-nav">
              <li className="has-user-block">
                {/* You can render your SidebarUserBlock here if needed */}
              </li>

              {userMenu.map((item, i) => {
                const type = itemType(item);
                if (type === "heading")
                  return <SidebarItemHeader item={item} key={i} />;
                if (type === "menu")
                  return (
                      <SidebarItem
                          key={i}
                          item={item}
                          isActive={routeActive(item.path)}
                      />
                  );
                if (type === "submenu")
                  return (
                      <SidebarSubItem
                          key={i}
                          item={item}
                          isOpen={collapse[item.name]}
                          handler={() => toggleItemCollapse(item.name)}
                          isActive={routeActive(getSubRoutes(item))}
                      >
                        <SidebarSubHeader item={item} />
                        {item.submenu.map((subitem, j) => (
                            <SidebarItem
                                key={j}
                                item={subitem}
                                isActive={routeActive(subitem.path)}
                            />
                        ))}
                      </SidebarSubItem>
                  );
                return null;
              })}
            </ul>
          </nav>
        </div>
      </aside>
  );
};

Sidebar.propTypes = {
  actions: propTypes.object,
  settings: propTypes.object,
  token: propTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  settings: state.settings,
  token: state.token.userData, // should be an array!
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
